import React from 'react'

import './Table.css'

import {
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import DebouncedInput from './DebounceInput'
import fuzzyFilter from './Filters'
import Filter, { dateBetweenFilterFn } from './FilterComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'

function Table({ data, columns, tableClass, containerClass, headerClass, filtersHeaderClass, enableGlobalFilter, handleRowSelectionChange }) {

    const [columnFilters, setColumnFilters] = React.useState(
        []
    )
    const [globalFilter, setGlobalFilter] = React.useState('')

    const table = useReactTable({
        data,
        columns,
        autoResetFilters: false,
        filterFns: {
            fuzzy: fuzzyFilter,
            dateBetweenFilterFn,
        },
        state: {
            columnFilters,
        },
        // enableRowSelection: true,
        // onRowSelectionChange: handleRowSelectionChange,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: true,
        debugHeaders: true,
        debugColumns: true,
    })

    // React.useEffect(() => {
    //   if (table.getState().columnFilters[0]?.id === 'fullName') {
    //     if (table.getState().sorting[0]?.id !== 'fullName') {
    //       table.setSorting([{ id: 'fullName', desc: false }])
    //     }
    //   }
    // }, [table.getState().columnFilters[0]?.id])

    return (
        <>
            <div className={`w-100 ${containerClass}`}>
                {enableGlobalFilter && (
                    <div>
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={value => setGlobalFilter(value)}
                            className="p-2 mb-2 form-control form-control-sm"
                            placeholder="Search all columns..."
                        />
                    </div>
                )}
                <div className="h-2" />
                <table className={`table ${tableClass}`}>
                    <thead className={`${headerClass}`}>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} >
                                {headerGroup.headers.map(header => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan} className='border-bottom-0'>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    <div
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? 'cursor-pointer select-none py-2 px-1'
                                                                : 'py-2 px-1',
                                                            onClick: header.column.getToggleSortingHandler(),
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: <FontAwesomeIcon className='ms-2 text-accent2' icon={faSortUp} />,
                                                            desc: <FontAwesomeIcon className='ms-2 text-accent2' icon={faSortDown} />,
                                                        }[header.column.getIsSorted()] ??
                                                            (header.column.getCanSort() ? <FontAwesomeIcon className='ms-2 text-accent2' icon={faSort} /> : null)
                                                        }
                                                    </div>
                                                    {/* {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null} */}
                                                </>
                                            )}
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                    <thead className={`${filtersHeaderClass}`}>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan} className=''>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    {header.column.getCanFilter() ? (
                                                        <div>
                                                            <Filter column={header.column} table={table} />
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="table-group-divider">
                        {table.getRowModel().rows.map(row => {
                            return (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => {
                                        return (
                                            <td key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                {/* <div>{table.getPrePaginationRowModel().rows.length} Rows</div> */}
            </div>
            <div className="d-flex align-items-center gx-2 mt-3">
                <nav className=''>
                    <ul className={`pagination m-0 ${tableClass.includes('table-sm') ? 'pagination-sm' : ''}`}>
                        <li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Previous" onClick={() => table.setPageIndex(0)} >
                                <span aria-hidden="true">&laquo;</span>
                            </a>
                        </li>
                        <li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Previous" onClick={() => table.previousPage()} >
                                <span aria-hidden="true">&lt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Next" onClick={() => table.nextPage()} >
                                <span aria-hidden="true">&gt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Next" onClick={() => table.setPageIndex(table.getPageCount() - 1)} >
                                <span aria-hidden="true">&raquo;</span>
                            </a>
                        </li>
                    </ul>
                </nav>
                <span className="d-flex align-items-center gx-1 ms-2">
                    <div>Page</div>
                    <strong>
                        {table.getState().pagination.pageIndex + 1} of{' '}
                        {table.getPageCount()}
                    </strong>
                </span>
                <span className="d-flex align-items-center gx-1 ms-2">
                    | Go to page:
                    <input
                        type="number"
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0
                            table.setPageIndex(page)
                        }}
                        className={`form-control w-auto ms-1 ${tableClass.includes('table-sm') ? 'form-control-sm' : ''}`}
                    />
                </span>
                <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                        table.setPageSize(Number(e.target.value))
                    }}
                    className={`form-select w-auto ms-2 ${tableClass.includes('table-sm') ? 'form-select-sm' : ''}`}
                >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                        <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </option>
                    ))}
                </select>
            </div>
        </>
    )
}

export default Table;